import type { Settings, Soundscape } from "../app/Settings";

const WAVE: Record<Soundscape, OscillatorType> = { water: "sine", minimal: "sine", wood: "triangle", crystal: "sine", ambient: "sine" };

export class TourSonifier {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private echoInput: GainNode | null = null;
  constructor(private settings: Settings) {}

  async unlock(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.connect(this.context.destination);

      // A bounded feedback delay lets one droplet decay underneath the next
      // landing without allocating a new delay graph for every move.
      const delay = this.context.createDelay(1);
      const feedback = this.context.createGain();
      const echoTone = this.context.createBiquadFilter();
      const wet = this.context.createGain();
      this.echoInput = this.context.createGain();
      delay.delayTime.value = .185;
      feedback.gain.value = .34;
      echoTone.type = "lowpass";
      echoTone.frequency.value = 2600;
      wet.gain.value = .42;
      this.echoInput.connect(delay);
      delay.connect(echoTone);
      echoTone.connect(wet);
      wet.connect(this.master);
      echoTone.connect(feedback);
      feedback.connect(delay);
    }
    if (this.context.state === "suspended") await this.context.resume();
    this.syncVolume();
  }

  landing(square: number): void {
    if (!this.ready()) return;
    if (this.settings.soundscape === "water") { this.waterDrop(square); return; }
    const row = Math.floor(square / 8), col = square % 8;
    const semitone = [0, 2, 3, 5, 7, 9, 10, 12][(row + col) % 8] + Math.floor(row / 4) * 12;
    this.note(164.81 * 2 ** (semitone / 12), this.settings.soundscape === "ambient" ? .42 : .16, .55);
  }

  cue(continuation: boolean): void {
    if (!this.ready()) return;
    const root = continuation ? 293.66 : 261.63;
    this.note(root, .32, .42); this.note(root * 1.5, .42, .22, .08);
  }

  complete(closed: boolean): void {
    if (!this.ready()) return;
    const root = closed ? 220 : 196;
    [1, 1.25, 1.5, closed ? 2 : 1.875].forEach((ratio, i) => this.note(root * ratio, .75, .18, i * .055));
  }

  ui(): void { if (this.ready()) this.note(520, .06, .09); }
  syncVolume(): void { if (this.master) this.master.gain.setTargetAtTime(this.settings.soundEnabled ? this.settings.soundVolume : 0, this.context?.currentTime ?? 0, .02); }
  suspendForHidden(hidden: boolean): void { if (!this.context) return; hidden ? void this.context.suspend() : this.settings.soundEnabled && void this.context.resume(); }

  private ready(): boolean { this.syncVolume(); return !!this.context && this.settings.soundEnabled && !this.settings.paused && document.visibilityState !== "hidden"; }
  private note(frequency: number, duration: number, level: number, delay = 0): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator(); const gain = this.context.createGain();
    oscillator.type = WAVE[this.settings.soundscape]; oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(.0001, now); gain.gain.exponentialRampToValueAtTime(Math.max(.001, level), now + .012); gain.gain.exponentialRampToValueAtTime(.0001, now + duration);
    oscillator.connect(gain); gain.connect(this.master); oscillator.start(now); oscillator.stop(now + duration + .03);
  }

  private waterDrop(square: number): void {
    if (!this.context || !this.master || !this.echoInput) return;
    const now = this.context.currentTime;
    const row = Math.floor(square / 8), col = square % 8;
    const startFrequency = 760 + row * 23 + col * 17;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const tone = this.context.createBiquadFilter();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(startFrequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(startFrequency * .46, now + .095);
    tone.type = "bandpass";
    tone.frequency.value = 1180 + (row - col) * 24;
    tone.Q.value = 1.8;
    gain.gain.setValueAtTime(.0001, now);
    gain.gain.exponentialRampToValueAtTime(.32, now + .004);
    gain.gain.exponentialRampToValueAtTime(.0001, now + .16);
    oscillator.connect(tone);
    tone.connect(gain);
    gain.connect(this.master);
    gain.connect(this.echoInput);
    oscillator.start(now);
    oscillator.stop(now + .18);
  }
}
